import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import copy from 'copy-to-clipboard';
import { dadosDoPix } from '../../global/pix';
import { useFormik } from 'formik';
import { useCallback, useState } from 'react';
import useHandleErrors from '../../hooks/useHandleErrors';
import LoadingButton from '@mui/lab/LoadingButton';
import axios from 'axios';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent from '../../components/LoadingContent';
import server from '../../global/server';
import * as Yup from 'yup';
import { validaCPF } from '../../global/cpfCnpj';
import { getMonth, getYear } from 'date-fns';
import DateInput from '../../components/DateInput/Index';
import { useSnackbar } from 'notistack';
import PaymentIcon from '@mui/icons-material/Payment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ConflictDialog from '../../components/ConflictDialog';

const month = getMonth(new Date());
const regexMonth =
    month <= 9
        ? new RegExp(`0[${month + 1}-9]|1[0-2]`)
        : new RegExp(`${String(month).split('').join('[')}-2]`);

const PaymentSchema = Yup.object().shape({
    document: Yup.string()
        .matches(/^[0-9]*$/, 'Use apenas números')
        .length(11, 'Documento incompleto')
        .max(11, 'Dado inválido')
        .test('invalid-cpf', 'Documento inválido', cpf => validaCPF(cpf))
        .required('Campo obrigatório'),
    birthdate: Yup.date().required('Campo obrigatório'),
    zipcode: Yup.string().required('Campo obrigatório'),
    address: Yup.string().required('CEP inválido ou endereço ausente.'),
    complement: Yup.string().required('Campo obrigatório'),
    phone: Yup.string()
        .length(11, 'Telefone incompleto')
        .matches(/^(\d{2})\D*(\d{5}|\d{4})\D*(\d{4})$/, 'Telefone inválido')
        .required('Campo obrigatório'),
    payment_method: Yup.string().required('Campo obrigatório'),
    holder_name: Yup.string().max(30, 'Máximo 30 caracteres').when('payment_method', {
        is: v => v === 'credit_card',
        then: schema => schema.required('Campo obrigatório'),
    }),
    card_number: Yup.string()
        .test('card-length', 'Número do cartão incompleto', val => {
            if (!val) return true;
            const digits = val.replace(/\D/g, '');
            return digits.length === 16;
        })
        .when('payment_method', {
            is: v => v === 'credit_card',
            then: schema => schema.required('Campo obrigatório'),
        }),
    cvv: Yup.string()
        .length(3, 'CVV deve ter 3 digitos')
        .matches(/^\d{3}$/, 'Apenas números')
        .when('payment_method', {
            is: v => v === 'credit_card',
            then: schema => schema.required('Campo obrigatório'),
        }),
    exp_month: Yup.string()
        .min(2, 'Incompleto')
        .max(2, 'Mês inválido')
        .when('payment_method', {
            is: v => v === 'credit_card',
            then: schema => schema.required('Campo obrigatório'),
        })
        .when('exp_year', {
            is: value => Number(value) == getYear(new Date()),
            then: schema => schema.matches(regexMonth, 'Cartão vencido'),
            otherwise: schema => schema.matches(/0[1-9]|1[0-2]/, 'Mês inválido'),
        }),
    exp_year: Yup.number()
        .moreThan(getYear(new Date()) - 1, 'Cartão vencido')
        .when('payment_method', {
            is: v => v === 'credit_card',
            then: schema => schema.required('Campo obrigatório'),
        }),
});

const SectionCard = ({ children }) => (
    <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        p: { xs: '20px 16px', sm: '28px 24px' },
    }}>
        {children}
    </Box>
);

const SectionTitle = ({ children, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600} color="#333">
            {children}
        </Typography>
    </Box>
);

// Helper: so mostra erro se o campo foi tocado
const fieldError = (formik, name) => formik.touched[name] && Boolean(formik.errors[name]);
const fieldHelperText = (formik, name) => formik.touched[name] && formik.errors[name];

export default () => {
    const params = useParams();
    const [errs, setErrs] = useState();
    useHandleErrors(errs);
    const { enqueueSnackbar } = useSnackbar();
    const navigation = useNavigate();
    const location = useLocation();
    const { data, isLoading, error } = useFetch('/search', { id: Number(params.id) });
    const meeting = data?.meetings ? data.meetings.find(m => m.id === Number(params.id)) : data;

    if (!location.state) {
        return (
            <Box sx={{ maxWidth: 720, mx: 'auto', p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">Dados do encontro não encontrados.</Typography>
                <LoadingButton variant="outlined" sx={{ mt: 2 }} onClick={() => navigation(-1)}>Voltar</LoadingButton>
            </Box>
        );
    }

    const formik = useFormik({
        validationSchema: PaymentSchema,
        validateOnBlur: true,
        validateOnChange: false,
        initialValues: {
            schedule_date: location.state?.schedule_date || '',
            meeting_schedule_id: '',
            meeting_id: location.state?.meeting_id || params.id,
            payment_method: 'credit_card',
            birthdate: '',
            document: '',
            phone: '',
            zipcode: '',
            address: '',
            complement: '',
            city: '',
            state: '',
            number: '',
            card_number: '',
            holder_name: '',
            exp_month: '',
            exp_year: '',
            cvv: '',
        },
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                // Verificar conflitos de horario
                const { data: conflictCheck } = await server.get('/booking/conflicts', {
                    params: { datetime: values.schedule_date },
                });
                if (conflictCheck.hasConflict) {
                    setConflictData(conflictCheck);
                    setConflictOpen(true);
                    setSubmitting(false);
                    return;
                }

                const body = {
                    meeting_id: values.meeting_id,
                    datetime: values.schedule_date,
                    document: values.document,
                    zipcode: values.zipcode,
                    address: values.address,
                    city: values.city,
                    state: values.state,
                    country: 'BR',
                    number: values.number || '0',
                    complement: values.complement,
                    country_code: '55',
                    area_code: String(values.phone).substring(0, 2),
                    number_phone: String(values.phone).substring(2),
                    birthdate: values.birthdate,
                    payment_method: values.payment_method,
                };

                if (values.payment_method === 'credit_card') {
                    try {
                        const { data: card_data } = await axios.post(
                            'https://api.pagar.me/core/v5/tokens',
                            {
                                type: 'card',
                                card: {
                                    number: values.card_number.replace(/\D/g, ''),
                                    holder_name: values.holder_name,
                                    holder_document: values.document,
                                    exp_month: values.exp_month,
                                    exp_year: values.exp_year,
                                    cvv: values.cvv,
                                },
                            },
                            { params: { appId: process.env.REACT_APP_PG_PUBLIC_KEY } },
                        );
                        body.card_token = card_data.id;
                    } catch (tokenError) {
                        // Em DEV_MODE o backend aceita sem token, envia mesmo assim
                        console.warn('Falha na tokenização do cartão, enviando sem token:', tokenError.message);
                    }
                }

                const { data } = await server.post('/booking', body, {
                    timeout: 50000,
                    timeoutErrorMessage: 'Tempo de operação esgotado',
                });
                setSuccessData(data.data);
            } catch (error) {
                if (error?.response?.data) {
                    setErrs(error);
                } else if (error?.message) {
                    enqueueSnackbar(error.message, { variant: 'error' });
                } else {
                    enqueueSnackbar('Cartão inválido. Revise os dados e tente novamente.', { variant: 'error' });
                }
                setSubmitting(false);
            }
        },
    });

    const [successData, setSuccessData] = useState(null);
    const [conflictData, setConflictData] = useState(null);
    const [conflictOpen, setConflictOpen] = useState(false);
    const [searchingZipCode, setSearching] = useState(false);
    const searchPostalCode = useCallback(async (e) => {
        formik.handleBlur(e);
        if (String(formik.values.zipcode).length === 8) {
            try {
                const { data } = await axios.get(`https://viacep.com.br/ws/${formik.values.zipcode}/json/`);
                if (data.erro) throw 'Erro';
                formik.setFieldValue('address', `${data.logradouro}, ${data.bairro}`, true);
                formik.setFieldValue('city', data.localidade, true);
                formik.setFieldValue('state', data.uf, true);
            } catch {
                formik.setFieldError('zipcode', 'CEP não encontrado');
            }
        }
    }, [formik.values.zipcode]);

    if (isLoading) return <LoadingContent />;

    if (!meeting) {
        return (
            <Box sx={{ maxWidth: 720, mx: 'auto', p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">Encontro não encontrado.</Typography>
                <LoadingButton variant="outlined" sx={{ mt: 2 }} onClick={() => navigation(-1)}>Voltar</LoadingButton>
            </Box>
        );
    }

    const isCreditCard = formik.values.payment_method === 'credit_card';
    // Pix so e pago depois: o QR precisa aparecer aqui mesmo, antes do usuario
    // sair da tela
    const pix = dadosDoPix(successData?.payment_details);
    const pixPendente = Boolean(pix?.qr_code || pix?.qr_code_url);
    const hostUser = meeting?.Profile?.User;
    const scheduleDate = location.state?.schedule_date
        ? new Date(location.state.schedule_date).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
        : '';

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 3,
        }}>
            {/* Header - Resumo do encontro */}
            <SectionCard>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        src={hostUser?.profileImage}
                        alt={hostUser?.name}
                        sx={{ width: 56, height: 56, border: '2px solid #e0e0e0' }}
                    >
                        {hostUser?.name?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {meeting.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Anfitrião: @{hostUser?.username}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Chip
                        size="small"
                        label={meeting.pricing ? `R$ ${Number(meeting.pricing).toFixed(2).replace('.', ',')}` : 'Gratuito'}
                        sx={{ bgcolor: meeting.pricing ? '#fef3c7' : '#e8f5e9', color: meeting.pricing ? '#b45309' : '#2e7d32', fontWeight: 600 }}
                    />
                    {scheduleDate && (
                        <Chip size="small" label={scheduleDate} sx={{ bgcolor: '#f5f5f5' }} />
                    )}
                </Stack>
            </SectionCard>

            <form onSubmit={formik.handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Dados pessoais */}
                <SectionCard>
                    <SectionTitle>Dados pessoais</SectionTitle>
                    <Stack spacing={2}>
                        <TextField
                            label="CPF"
                            name="document"
                            size="small"
                            fullWidth
                            value={formik.values.document}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={fieldError(formik, 'document')}
                            helperText={fieldHelperText(formik, 'document')}
                            inputProps={{ maxLength: 11 }}
                        />
                        <DateInput
                            label="Data de nascimento"
                            name="birthdate"
                            obrigatorio
                            naoFuturo
                            value={formik.values.birthdate}
                            onChange={iso => formik.setFieldValue('birthdate', iso)}
                            onBlur={() => formik.setFieldTouched('birthdate', true)}
                            erro={fieldError(formik, 'birthdate')}
                            textoAuxiliar={fieldHelperText(formik, 'birthdate')}
                        />
                        <TextField
                            label="Celular"
                            name="phone"
                            size="small"
                            fullWidth
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={fieldError(formik, 'phone')}
                            helperText={fieldHelperText(formik, 'phone')}
                            inputProps={{ maxLength: 11 }}
                            placeholder="11999999999"
                        />
                    </Stack>
                </SectionCard>

                {/* Forma de pagamento */}
                <SectionCard>
                    <SectionTitle icon={<PaymentIcon sx={{ fontSize: 20, color: '#006a61' }} />}>
                        Forma de pagamento
                    </SectionTitle>
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Metodo</InputLabel>
                            <Select
                                label="Metodo"
                                name="payment_method"
                                value={formik.values.payment_method}
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="credit_card">Cartão de crédito</MenuItem>
                                <MenuItem value="pix">PIX</MenuItem>
                            </Select>
                        </FormControl>

                        {isCreditCard && (
                            <>
                                <TextField
                                    label="Número do cartão"
                                    name="card_number"
                                    size="small"
                                    fullWidth
                                    placeholder="0000 0000 0000 0000"
                                    value={formik.values.card_number}
                                    onChange={(e) => {
                                        const digits = e.target.value.replace(/\D/g, '').slice(0, 16);
                                        const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
                                        formik.setFieldValue('card_number', formatted);
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={fieldError(formik, 'card_number')}
                                    helperText={fieldHelperText(formik, 'card_number')}
                                    inputProps={{ maxLength: 19, inputMode: 'numeric' }}
                                />
                                <TextField
                                    label="Nome do titular"
                                    name="holder_name"
                                    size="small"
                                    fullWidth
                                    value={formik.values.holder_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={fieldError(formik, 'holder_name')}
                                    helperText={fieldHelperText(formik, 'holder_name') || 'Máximo 30 caracteres'}
                                    inputProps={{ maxLength: 30 }}
                                />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                    <TextField
                                        label="Mês"
                                        name="exp_month"
                                        size="small"
                                        placeholder="MM"
                                        value={formik.values.exp_month}
                                        onChange={(e) => {
                                            const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                                            formik.setFieldValue('exp_month', v);
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={fieldError(formik, 'exp_month')}
                                        helperText={fieldHelperText(formik, 'exp_month')}
                                        inputProps={{ maxLength: 2, inputMode: 'numeric' }}
                                    />
                                    <TextField
                                        label="Ano"
                                        name="exp_year"
                                        size="small"
                                        placeholder="AAAA"
                                        value={formik.values.exp_year}
                                        onChange={(e) => {
                                            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                            formik.setFieldValue('exp_year', v);
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={fieldError(formik, 'exp_year')}
                                        helperText={fieldHelperText(formik, 'exp_year')}
                                        inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                                    />
                                    <TextField
                                        label="CVV"
                                        name="cvv"
                                        size="small"
                                        value={formik.values.cvv}
                                        onChange={(e) => {
                                            const v = e.target.value.replace(/\D/g, '').slice(0, 3);
                                            formik.setFieldValue('cvv', v);
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={fieldError(formik, 'cvv')}
                                        helperText={fieldHelperText(formik, 'cvv')}
                                        inputProps={{ maxLength: 3, inputMode: 'numeric' }}
                                    />
                                </Box>
                            </>
                        )}
                    </Stack>
                </SectionCard>

                {/* Endereco de cobranca */}
                <SectionCard>
                    <SectionTitle>Endereço de cobrança</SectionTitle>
                    <Stack spacing={2}>
                        <TextField
                            label="CEP"
                            name="zipcode"
                            size="small"
                            fullWidth
                            value={formik.values.zipcode}
                            onChange={formik.handleChange}
                            onBlur={searchPostalCode}
                            error={fieldError(formik, 'zipcode') || fieldError(formik, 'address')}
                            helperText={
                                (fieldError(formik, 'zipcode') || fieldError(formik, 'address'))
                                    ? `${formik.touched.zipcode && formik.errors.zipcode || ''} ${formik.touched.address && formik.errors.address || ''}`.trim()
                                    : formik.values.address ? `${formik.values.address}, ${formik.values.city}-${formik.values.state}` : ''
                            }
                            inputProps={{ maxLength: 8 }}
                            InputProps={{
                                endAdornment: searchingZipCode ? (
                                    <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                                ) : null,
                            }}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 1fr' }, gap: 2 }}>
                            <TextField
                                label="Complemento"
                                name="complement"
                                size="small"
                                fullWidth
                                value={formik.values.complement}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={fieldError(formik, 'complement')}
                                helperText={fieldHelperText(formik, 'complement')}
                            />
                            <TextField
                                label="Número"
                                name="number"
                                size="small"
                                fullWidth
                                value={formik.values.number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={fieldError(formik, 'number')}
                                helperText={fieldHelperText(formik, 'number')}
                            />
                        </Box>
                    </Stack>
                </SectionCard>

                {/* Botao de pagar */}
                <LoadingButton
                    type="submit"
                    fullWidth
                    loading={formik.isSubmitting}
                    variant="contained"
                    disabled={formik.isSubmitting}
                    sx={{
                        bgcolor: '#006a61',
                        borderRadius: '8px',
                        height: 48,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': { bgcolor: '#005a52' },
                    }}
                >
                    {isCreditCard ? 'Pagar encontro' : 'Pagar com PIX'}
                </LoadingButton>

                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                    <LockOutlinedIcon sx={{ fontSize: 14, color: '#999' }} />
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                        Pagamento seguro processado pela Pagar.me
                    </Typography>
                </Stack>

                {/* A Politica de Compra e Pagamento passa a valer exatamente
                    aqui: e a compra que a submete ao usuario. Link, e nao
                    checkbox: o aceite dos Termos ja acontece no cadastro. */}
                <Typography variant="caption" color="text.secondary" textAlign="center">
                    Ao concluir a compra você concorda com a{' '}
                    <Link
                        component={RouterLink}
                        to="/politicas/compra-e-pagamento"
                        target="_blank"
                        rel="noopener"
                        sx={{ color: '#006a61', fontWeight: 600 }}
                    >
                        Política Geral de Compra e Pagamento
                    </Link>
                    , que trata de reembolso, arrependimento e parcelamento.
                </Typography>
            </form>

            <ConflictDialog
                open={conflictOpen}
                onClose={() => setConflictOpen(false)}
                conflicts={conflictData}
                onResolved={() => formik.handleSubmit()}
            />

            <Dialog
                open={Boolean(successData)}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: 400 } }}
            >
                <DialogContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
                    {pixPendente ? (
                        <>
                            <QrCode2Icon sx={{ fontSize: 64, color: '#006a61', mb: 2 }} />
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                Falta pagar o Pix
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                                Escaneie o QR Code abaixo ou copie a chave para concluir o pagamento
                                do encontro com <strong>@{hostUser?.username}</strong>
                                {scheduleDate ? ` em ${scheduleDate}` : ''}. O encontro só é
                                confirmado depois que o pagamento cair.
                            </Typography>
                            {Boolean(pix?.qr_code_url) && (
                                <Box
                                    component="img"
                                    src={pix.qr_code_url}
                                    alt="QR Code do Pix"
                                    sx={{ width: 220, height: 220, mx: 'auto', mb: 2, display: 'block' }}
                                />
                            )}
                            {Boolean(pix?.qr_code) && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        copy(pix.qr_code);
                                        enqueueSnackbar('Chave copiada');
                                    }}
                                    sx={{
                                        borderRadius: '8px',
                                        height: 44,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        mb: 1.5,
                                        color: '#006a61',
                                        borderColor: '#006a61',
                                    }}
                                >
                                    Copiar chave Pix
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                Compra realizada com sucesso!
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                                Seu encontro com <strong>@{hostUser?.username}</strong> foi confirmado
                                para {scheduleDate}. Você pode acompanhar os detalhes na sua lista de encontros.
                            </Typography>
                        </>
                    )}
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => navigation(`/encontros/${successData?.id}/detalhes`, { replace: true })}
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        {pixPendente ? 'Já paguei, ver detalhes' : 'Ver detalhes do encontro'}
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
